import Vue from 'vue';
import Vuex from 'vuex';
import { getAjaxList } from '@/api/home';
import homeMock from '@/mock/home';

Vue.use(Vuex);

let latestArticlesRequestToken = 0;
let latestLiveSlidesRequestToken = 0;
let latestAllSectionsRequestToken = 0;

async function fetchCategoryArticles(category, pageSize) {
  if (!category || !category.listId) {
    return [];
  }

  const response = await getAjaxList({
    id: category.listId,
    page: 1,
    pagesize: pageSize
  });
  const list = response && response.data ? response.data : [];

  return list.slice().sort((firstItem, nextItem) => {
    return Number(nextItem && nextItem.published ? nextItem.published : 0) -
      Number(firstItem && firstItem.published ? firstItem.published : 0);
  });
}

function getCategoryPageSize(category) {
  const pageSize = Number(category && category.pageSize ? category.pageSize : 10);

  if (!pageSize || pageSize < 1) {
    return 10;
  }

  return pageSize;
}

function cloneList(list) {
  return (list || []).map((item) => ({ ...item }));
}

function getLocalPageData() {
  return {
    share: { ...(homeMock.share || {}) },
    hero: { ...(homeMock.hero || {}) },
    live: { ...(homeMock.live || {}) },
    quickEntries: cloneList(homeMock.quickEntries),
    categories: cloneList(homeMock.categories),
    highlight: { ...(homeMock.highlight || {}) },
    articles: []
  };
}

export default new Vuex.Store({
  state: {
    loading: false,
    activeTab: 0,
    activeBottom: 'schedule',
    pageData: getLocalPageData(),
    liveSlides: [],
    allTabSections: []
  },
  getters: {
    currentCategory(state) {
      const currentItem = state.pageData.categories[state.activeTab];
      return currentItem ? currentItem.key : 'all';
    },
    filteredArticles(state) {
      return state.pageData.articles;
    },
    heroLiveSlides(state) {
      return state.liveSlides;
    },
    allSections(state) {
      return state.allTabSections;
    }
  },
  mutations: {
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_PAGE_DATA(state, payload) {
      state.pageData = payload;
    },
    SET_ARTICLES(state, payload) {
      state.pageData = {
        ...state.pageData,
        articles: payload
      };
    },
    SET_LIVE_SLIDES(state, payload) {
      state.liveSlides = payload;
    },
    SET_ALL_TAB_SECTIONS(state, payload) {
      state.allTabSections = payload;
    },
    SET_ACTIVE_TAB(state, value) {
      state.activeTab = value;
    },
    SET_ACTIVE_BOTTOM(state, value) {
      state.activeBottom = value;
    }
  },
  actions: {
    async fetchHomePageData({ commit }) {
      commit('SET_LOADING', true);

      try {
        const data = getLocalPageData();
        commit('SET_PAGE_DATA', data);
        return data;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async fetchLiveSlides({ commit, state }) {
      const requestToken = ++latestLiveSlidesRequestToken;
      const categories = state.pageData.categories || [];
      const liveCategory = categories.find((item) => item.key === 'live') || {};
      const liveConfig = state.pageData.live || {};
      const listId = liveConfig.listId || liveCategory.listId;

      if (!listId) {
        commit('SET_LIVE_SLIDES', []);
        return [];
      }

      try {
        const slides = await fetchCategoryArticles(
          {
            key: liveCategory.key || 'live',
            label: liveCategory.label || '赛程直播',
            listId
          },
          getCategoryPageSize({ pageSize: liveConfig.pageSize || 5 })
        );

        if (requestToken !== latestLiveSlidesRequestToken) {
          return state.liveSlides;
        }

        commit('SET_LIVE_SLIDES', slides);
        return slides;
      } catch (error) {
        console.error('fetchLiveSlides error:', error);

        if (requestToken !== latestLiveSlidesRequestToken) {
          return state.liveSlides;
        }

        commit('SET_LIVE_SLIDES', []);
        return [];
      }
    },
    async fetchAllTabSections({ commit, state }) {
      const requestToken = ++latestAllSectionsRequestToken;
      const categories = (state.pageData.categories || []).filter((item) => item.key !== 'all');

      if (!categories.length) {
        commit('SET_ALL_TAB_SECTIONS', []);
        return [];
      }

      try {
        const sections = await Promise.all(
          categories.map(async (category) => {
            const articles = category.listId
              ? await fetchCategoryArticles(category, getCategoryPageSize(category))
              : [];

            return {
              category,
              articles
            };
          })
        );

        if (requestToken !== latestAllSectionsRequestToken) {
          return state.allTabSections;
        }

        commit('SET_ALL_TAB_SECTIONS', sections);
        return sections;
      } catch (error) {
        console.error('fetchAllTabSections error:', error);

        if (requestToken !== latestAllSectionsRequestToken) {
          return state.allTabSections;
        }

        commit('SET_ALL_TAB_SECTIONS', []);
        return [];
      }
    },
    async fetchArticlesByTab({ commit, state }, tabIndex = state.activeTab) {
      const requestToken = ++latestArticlesRequestToken;
      const categories = state.pageData.categories || [];
      const activeCategory = categories[tabIndex];

      if (!activeCategory) {
        commit('SET_ARTICLES', []);
        return [];
      }

      try {
        if (activeCategory.key === 'all') {
          const articles = await fetchCategoryArticles(
            activeCategory,
            getCategoryPageSize(activeCategory)
          );

          if (requestToken !== latestArticlesRequestToken) {
            return state.pageData.articles;
          }

          commit('SET_ARTICLES', articles);
          return articles;
        }

        if (!activeCategory.listId) {
          if (requestToken !== latestArticlesRequestToken) {
            return state.pageData.articles;
          }

          commit('SET_ARTICLES', []);
          return [];
        }

        const articles = await fetchCategoryArticles(
          activeCategory,
          getCategoryPageSize(activeCategory)
        );

        if (requestToken !== latestArticlesRequestToken) {
          return state.pageData.articles;
        }

        commit('SET_ARTICLES', articles);
        return articles;
      } catch (error) {
        console.error('fetchArticlesByTab error:', error);

        if (requestToken !== latestArticlesRequestToken) {
          return state.pageData.articles;
        }

        commit('SET_ARTICLES', []);
        return [];
      }
    }
  }
});
